import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { BREAKPOINTS, glyphs, Link, styles as S } from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'
import * as api from '@owl-nest/api-client/next'

import { DisplayMode } from '../../../../context'
import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'
import { CreateButton } from '../CreateButton'
import { EditButton } from '../EditButton'
import { MIDDLE_TABLET_LAPTOP_BREAKPOINT } from '../../constants'

type HeadingProps = {
  children: React.ReactNode
}

export function Heading({ children }: HeadingProps): React.ReactElement<HeadingProps> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const isEditMode = React.useContext(DisplayMode) === 'edit' && !model.project.refused(project)
  const userConfig = env.useUserEnv()

  const titleAndSubtitle = (
    <>
      <Title $isEditMode={isEditMode}>
        <S.heading.L as="h1">{model.project.name<projectModel.ExtraFields>(project)}</S.heading.L>
        <S.heading.XL as="h1">{model.project.name<projectModel.ExtraFields>(project)}</S.heading.XL>
      </Title>
      <S.copy.M.light>{model.project.subtitle<projectModel.ExtraFields>(project)}</S.copy.M.light>
    </>
  )

  const editView = (
    <>
      {model.project.name<projectModel.ExtraFields>(project) ? (
        <>
          {titleAndSubtitle}
          <EditButtonWrapper $isEditMode={isEditMode}>
            <EditButton link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?name`} />
          </EditButtonWrapper>
        </>
      ) : (
        <EditViewStyled $isEditMode={isEditMode}>
          <CreateButton
            center={true}
            createHandler={() => {
              document.location.href = `/projects/${project.id}/edit/layout/${userConfig.lang}/?name`
            }}
            cta={
              <AdviceLinkWrapper>
                <Link
                  onClick={() => {
                    document.location.href = getAdviceURL(userConfig.lang)
                  }}
                  tinted
                  withIcon
                >
                  <glyphs.stroke.Bulb size={15} />
                  {t('How to write a good title and subtitle')}
                </Link>
              </AdviceLinkWrapper>
            }
            title={
              <>
                <S.heading.M>{t('Add a title and subtitle')}</S.heading.M>
                <S.heading.L>{t('Add a title and subtitle')}</S.heading.L>
              </>
            }
          />
        </EditViewStyled>
      )}
    </>
  )

  return (
    <HeadingWrapper $isEditMode={isEditMode}>
      <Content $isEditMode={isEditMode}>{isEditMode ? <>{editView}</> : <>{titleAndSubtitle}</>}</Content>
      {children}
    </HeadingWrapper>
  )
}

function getAdviceURL(lang: api.Lang): string {
  let url
  switch (lang) {
    case 'en':
      url = 'https://community.ulule.com/topics/a-title-with-a-hook-17512/#main-31973'
      break

    case 'es':
      url = 'https://community.ulule.com/topics/titulo-y-subtitulo-del-proyecto-17522/#main-31991'
      break

    case 'fr':
      url = 'https://community.ulule.com/topics/un-titre-accrocheur-17509/#main-31967'
      break

    case 'it':
      url = 'https://community.ulule.com/topics/creare-titolo-sottotitolo-campagna-crowdfunding-17517/'
      break

    case 'nl':
      url = 'https://community.ulule.com/topics/een-titel-met-een-hoek-af-17514/#main-31975'
      break

    default:
      url = 'https://community.ulule.com/topics/a-title-with-a-hook-17512/#main-31973'
      break
  }

  return url
}

const Title = styled.span<{ $isEditMode: boolean }>`
  display: block;
  letter-spacing: 0;
  margin-bottom: 15px;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          margin-bottom: 8px;
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          margin-bottom: 8px;
        }
      `
    }
  }}

  ${S.heading.XL} {
    display: none;
  }
  ${S.heading.L} {
    display: block;
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} and (max-width: 1100px) {
          ${S.heading.XL} {
            display: block;
          }
          ${S.heading.L} {
            display: none;
          }
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} and (max-width: 1100px) {
          ${S.heading.XL} {
            display: block;
          }
          ${S.heading.L} {
            display: none;
          }
        }
      `
    }
  }}
`

const contentBreakpointStyle = css<{ $isEditMode: boolean }>`
  display: inline-block;
  margin: 0 auto;
  max-width: ${({ $isEditMode }) => ($isEditMode ? '70%' : '80%')};
  position: relative;
  width: auto;
  z-index: 2;
`

const Content = styled.div<{ $isEditMode: boolean }>`
  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${contentBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${contentBreakpointStyle};
        }
      `
    }
  }}
`

const EditViewStyled = styled.div<{ $isEditMode: boolean }>`
  text-align: center;

  ${S.heading.M} {
    display: none;
  }
  ${S.heading.L} {
    display: block;
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${S.heading.M} {
            display: block;
          }
          ${S.heading.L} {
            display: none;
          }
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${S.heading.M} {
            display: block;
          }
          ${S.heading.L} {
            display: none;
          }
        }
      `
    }
  }}
`

const headingWrapperBreakpointStyle = css`
  margin: 0 auto;
  padding: 20px 0;
  position: relative;
  text-align: center;
`

const HeadingWrapper = styled.div<{ $isEditMode: boolean }>`
  display: block;
  padding: 22px 10px;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${headingWrapperBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${headingWrapperBreakpointStyle};
        }
      `
    }
  }}
`

const editButtonWrapperBreakpointStyle = css`
  margin: 0px;
  position: absolute;
  right: -115px;
  top: 50%;
  transform: translateY(-50%);
`

const EditButtonWrapper = styled.div<{ $isEditMode: boolean }>`
  position: relative;
  margin-top: 5px;
  z-index: 2;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${editButtonWrapperBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${editButtonWrapperBreakpointStyle};
        }
      `
    }
  }}
`

const AdviceLinkWrapper = styled(S.copy.S.regular)`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: 4px;
`
